'use client';

import ErrorMessage from '@/components/atoms/ErrorMessage';

import AddToCartControls from '@/components/organisms/AddToCartControls';
import VariantSelector from '@/components/organisms/VariantSelector';
import { useJotaiCart } from '@/contexts/JotaiCartProvider';
import type { Product } from '@/types/custom';
import { getCartUrl } from '@/utils/cart-url';
import { getLowestPrice, getProductVariants } from '@/utils/product';
import { validateStock } from '@/utils/stock-validation';
import { useLocale, useTranslations } from 'next-intl';
import { useRouter } from 'next/navigation';
import { useCallback, useEffect, useState } from 'react';
import { Button } from './ui/button';

interface AddToCartFormProps {
    product: Product;
}

export default function AddToCartForm({ product }: AddToCartFormProps) {
    const t = useTranslations('Cart');
    const locale = useLocale();
    const router = useRouter();

    const [selectedVariantId, setSelectedVariantId] = useState<number | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);

    const { addItem, isLoading: cartIsLoading, error, items: cartItems } = useJotaiCart();

    const variants = getProductVariants(product) || [];

    // Helper function to get cart quantity for a variant
    const getCartQuantity = useCallback(
        (variantId: number): number => {
            const cartItem = cartItems.find((item) => item.purchasable.id === variantId);
            return cartItem ? cartItem.qty : 0;
        },
        [cartItems]
    );

    const handleVariantSelect = useCallback((id: number) => {
        setSelectedVariantId(id);
        setSuccessMessage(null); // Clear success message when changing variants
    }, []);

    const handleAddToCart = async () => {
        if (!selectedVariantId) return;

        setSuccessMessage(null); // Clear any previous success message

        try {
            await addItem(selectedVariantId, 1);

            // Only show success message if there's no error
            if (!error) {
                setSuccessMessage(t('productAddedToCart'));
            }
        } catch (localError) {
            console.error('Error adding to cart:', localError);
        }
    };

    // Auto-select single variant
    useEffect(() => {
        if (variants.length === 1 && !selectedVariantId) {
            handleVariantSelect(Number(variants[0].id));
        }
    }, [variants, handleVariantSelect, selectedVariantId]);

    // Clear success message when error occurs
    useEffect(() => {
        if (error) {
            setSuccessMessage(null);
        }
    }, [error]);

    // Determine button state and status message using centralized logic
    const getStatusInfo = () => {
        // For multiple variants without selection: disable button with "Choose a variant"
        if (variants.length > 1 && !selectedVariantId) {
            return {
                isDisabled: true,
                statusMessage: t('selectVariant'),
                showButton: true, // Show disabled button so user sees "Choose a variant" prompt
            };
        }

        // For single variants or selected variant
        const targetVariantId =
            selectedVariantId || (variants.length === 1 ? Number(variants[0].id) : null);
        if (!targetVariantId) return { isDisabled: true, statusMessage: null, showButton: false };

        const targetVariant = variants.find((v) => v.id === targetVariantId);
        if (!targetVariant) return { isDisabled: true, statusMessage: null, showButton: false };

        const cartQuantity = getCartQuantity(targetVariantId);

        const stockStatus = validateStock(
            { variant: targetVariant, currentCartQuantity: cartQuantity, requestedQuantity: 1 },
            {
                outOfStock: t('outOfStock'),
                inCart: (params) => t('inCart', params),
                lastItemInCart: t('lastItemInCart'),
                allItemsInCart: (params) => t('allItemsInCart', params),
                insufficientStock: (params) => t('insufficientStock', params),
                stockLimitExceededWithDetails: (params) =>
                    t('stockLimitExceededWithDetails', params),
            }
        );

        return {
            isDisabled: !stockStatus.canAdd,
            statusMessage: stockStatus.statusMessage,
            showButton: stockStatus.canAdd || stockStatus.isOutOfStock, // Hide button only when at max capacity
        };
    };

    const { isDisabled, statusMessage, showButton } = getStatusInfo();

    const cartUrl = getCartUrl(locale);

    if (getLowestPrice(product).lowestPrice === 0) {
        return null;
    }

    return (
        <div>
            <div className="bg-haug-ivory-100 p-4">
                {/* Show error message if there's an error */}
                {error && <ErrorMessage message={error} />}

                {/* Show status message if button is disabled and no error */}
                {!error && statusMessage && (
                    <div className="mb-4 rounded p-3 font-neue-haas text-gray-700 text-sm">
                        {statusMessage}
                    </div>
                )}

                {variants.length > 1 && <h2 className="h2 mb-4">{t('chooseVariant')}</h2>}

                {variants.length > 1 ? (
                    <VariantSelector
                        variants={variants}
                        selectedVariantId={selectedVariantId}
                        onVariantSelect={handleVariantSelect}
                        className="mb-4"
                        cartItems={cartItems}
                    />
                ) : (
                    <div className="mb-4 font-medium font-neue-haas text-gray-700">
                        {variants[0]?.name}
                        {variants[0]?.price && (
                            <span className="ml-2 font-bold font-neue-haas text-2xl text-gray-900">
                                €{variants[0].price.toFixed(2)}
                            </span>
                        )}
                    </div>
                )}

                {/* Only show AddToCartControls if showButton is true */}
                {showButton && !error && !successMessage && (
                    <AddToCartControls
                        onAddToCart={handleAddToCart}
                        isLoading={cartIsLoading}
                        isDisabled={isDisabled}
                    />
                )}

                {/* Show success message only if no error */}
                {!error && successMessage && (
                    <Button variant="success" onClick={() => router.push(cartUrl)}>
                        {t('viewCart')}
                    </Button>
                )}
            </div>
        </div>
    );
}
